import { Button } from '@design-system';
import { StripeEmbeddedCheckout } from '@/features/advisor-onboarding';
import { useClientStripeCheckoutSession } from '@/hooks/useClientStripeCheckoutSession';
import { fetchSessionForAuthRequest, selectSession } from '@/model';
import { useNavigateToPage } from '@/Router';
import { OnboardingD2CVirtualMeeting } from '@/pages/OnboardingD2CVirtualMeeting';
import { Check, ChevronLeft, Lock, Star } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { SessionType } from '@shared';

const PLAN_FEATURES = [
  'Unlock your personal cost projections',
  'Protect your loved ones',
  'Find the funding strategies that work for you',
];

export function D2CCheckoutPage({ clientId }: ClientIdProps) {
  const navigate = useNavigate();
  const navigateToPage = useNavigateToPage();
  const dispatch = useDispatch();
  const session = useSelector(selectSession);
  const clientAuthUserId = session.auth0Session?.clientUser?.appMetadata?.clientAuthUserId ?? '';
  const sessionRefetchForMissingAuthId = useRef(false);

  const returnPath = `/clients/${clientId}/onboarding/d2c-payment-success`;

  const { createCheckoutSession, isPending, clientSecret, isError, reset } =
    useClientStripeCheckoutSession({ clientAuthUserId, returnPath });

  // TODO: Clerk token refresh can replace auth0Session with { authUserId, accessToken } and drop
  // clientUser, so clientAuthUserId is empty until a full session refetch. Remove when Clerk merges
  // token updates into existing auth0Session (see SessionLoader storeAuth0Session).
  useEffect(() => {
    if (session.sessionType !== SessionType.client) {
      return;
    }
    if (session.isLoading || session.isInvalidating) {
      return;
    }
    if (clientAuthUserId || sessionRefetchForMissingAuthId.current) {
      return;
    }
    sessionRefetchForMissingAuthId.current = true;
    dispatch(fetchSessionForAuthRequest({ invalidating: true }));
  }, [clientAuthUserId, dispatch, session.isInvalidating, session.isLoading, session.sessionType]);

  useEffect(() => {
    if (clientAuthUserId && !clientSecret && !isPending && !isError) {
      createCheckoutSession();
    }
  }, [clientAuthUserId, clientSecret, isPending, isError, createCheckoutSession]);

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="flex min-h-screen flex-col items-center p-6 md:p-8">
      <div className="w-full max-w-6xl">
        <div className="grid gap-8 lg:grid-cols-2">
          <div className="space-y-4">
            <Button
              size="sm"
              variant="ghost"
              onClick={handleBack}
            >
              <ChevronLeft />
              Back
            </Button>
            <h1 className="mb-6 text-2xl font-bold text-gray-950">Waterlily Personal Plan</h1>

            <div className="rounded-lg bg-green-50 px-3 py-2 text-center">
              <p className="text-sm font-semibold text-green-800">🎉 Limited time only — 50% off</p>
            </div>

            <div className="flex items-center gap-2">
              <p className="text-xs text-gray-500">As featured in</p>
              <img
                src="/logos/nytimes.svg"
                alt="The New York Times"
                className="h-8"
              />
            </div>

            <div className="bg-primary/10 border-primary rounded-lg border-2 p-6">
              <h2 className="text-xl font-bold text-gray-950 sm:text-3xl">
                <span className="text-lg text-gray-400 line-through">$19.99</span> $9.99/year
                <span className="text-foreground"> - 50% Off</span>
              </h2>
              <p className="text-foreground mt-1 text-base">Finish your Long-Term Care plan now.</p>
              <ul className="mt-3 space-y-2">
                {PLAN_FEATURES.map(feature => (
                  <li
                    key={feature}
                    className="text-foreground flex items-center gap-2 text-sm"
                  >
                    <Check className="text-primary size-5 shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-purple-50 p-5">
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className="size-4 fill-amber-400 text-amber-400"
                  />
                ))}
              </div>
              <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-gray-700">
                Trusted by thousands of families and hundreds of financial professionals
              </p>
            </div>

            <Button
              size="lg"
              variant="outline"
              className="w-full rounded-full border-gray-400"
              onClick={() => navigateToPage(OnboardingD2CVirtualMeeting, { clientId })}
            >
              Access via Virtual Meeting — Free
            </Button>
          </div>

          <div>
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="bg-white px-6 py-4">
                <div className="flex items-center gap-2">
                  <Lock className="text-foreground size-5" />
                  <span className="font-semibold text-gray-950">Enter payment details</span>
                </div>
                <p className="text-foreground mt-1 text-sm">
                  Your card will be charged $9.99 for one year of access.
                </p>
              </div>
              <div className="min-h-105 flex flex-col bg-white">
                {isError ? (
                  <div className="flex flex-1 flex-col items-center justify-center gap-4 py-16">
                    <p className="text-foreground font-medium">
                      Something went wrong loading checkout.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        reset();
                        createCheckoutSession();
                      }}
                    >
                      Try again
                    </Button>
                  </div>
                ) : isPending || !clientSecret ? (
                  <div className="flex flex-1 flex-col items-center justify-center gap-4 py-16">
                    <div className="border-primary h-10 w-10 animate-spin rounded-full border-4 border-t-transparent" />
                    <p className="text-foreground font-medium">Preparing secure checkout...</p>
                  </div>
                ) : (
                  <div className="w-full">
                    <StripeEmbeddedCheckout clientSecret={clientSecret} />
                  </div>
                )}
              </div>
              <div className="bg-white px-6 py-4">
                <p className="text-foreground text-center text-xs">
                  By subscribing, you authorize Waterlily to charge you according to the terms until
                  you cancel.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
